import { useEffect, useState, useRef } from "react";
import axios from "axios";
import socket from "../../socket/socket";

const useDrivers = (filter, selectedDriver, setSelectedDriver) => {
  const [drivers, setDrivers] = useState([]);
  const [followTargetId, setFollowTargetId] = useState(null);
  const token = localStorage.getItem("token");

  const lastPositionsRef = useRef({});
  const fetchCache = useRef(new Set());

  /** ---------------- FILTER LOGIC ---------------- **/
  const applyHideFilterToDriver = (driver) => {
    let hide = false;

    if (driver.offline) hide = true;

    if (!hide && filter.deliveryCompany?.trim()) {
      if (
        !String(driver.delivaryCompanyId || "")
          .toLowerCase()
          .includes(filter.deliveryCompany.toLowerCase())
      )
        hide = true;
    }

    if (!hide && filter.driver?.trim()) {
      const s = filter.driver.toLowerCase();
      const name = (driver.name || "").toLowerCase();
      const phone = (driver.workPhone || "").toLowerCase();
      if (!name.includes(s) && !phone.includes(s)) hide = true;
    }

    const busy = driver.orders?.length > 0;

    if (!hide) {
      if (!filter.availableDriver && !busy) hide = true;
      if (!filter.inDeliveryDriver && busy) hide = true;
    }

    return { ...driver, hide };
  };

  /** ---------------- FETCH ALL DRIVERS ---------------- **/
  const fetchDrivers = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}track/drivers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.code === 1) {
        const raw = res.data.data || [];

        raw.forEach((d) => {
          if (d.location) lastPositionsRef.current[d._id] = d.location;
        });

        setDrivers(raw.map(applyHideFilterToDriver));
      }
    } catch (err) {
      console.error("Fetch Drivers Error:", err);
    }
  };

  /** ---------------- FETCH SINGLE DRIVER ---------------- **/
  const fetchSingleDriver = async (driverId) => {
    if (fetchCache.current.has(driverId)) return;
    fetchCache.current.add(driverId);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}track/driver/${driverId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.data) {
        const fullDriver = res.data.data;

        lastPositionsRef.current[driverId] = fullDriver.location;

        setDrivers((prev) => {
          const exists = prev.some((d) => d._id === driverId);

          if (exists) {
            return prev.map((d) =>
              d._id === driverId ? applyHideFilterToDriver(fullDriver) : d
            );
          }

          return [...prev, applyHideFilterToDriver(fullDriver)];
        });
      }
    } catch (err) {
      console.error("Single Driver Fetch Failed:", err);
    } finally {
      setTimeout(() => fetchCache.current.delete(driverId), 3000);
    }
  };

  /** ---------------- FOLLOW MODE ---------------- **/
  const updateSelectedDriver = async (driverId, follow = false) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}track/driver/${driverId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.data) {
        const freshDriver = {
          ...res.data.data,
          location: lastPositionsRef.current[driverId],
        };

        // Update popup
        setSelectedDriver(freshDriver);

        // Update global store
        setDrivers((prev) =>
          prev.map((d) =>
            d._id === driverId ? applyHideFilterToDriver(freshDriver) : d
          )
        );

        if (follow) setFollowTargetId(driverId);

        return;
      }

      console.warn("Driver refresh returned no data, using cached data.");
      const fallback = drivers.find((d) => d._id === driverId);
      setSelectedDriver(fallback || null);
    } catch (err) {
      console.warn("Driver refresh failed, using cached data.");
      const fallback = drivers.find((d) => d._id === driverId);
      setSelectedDriver(fallback || null);
    }
  };

  /** ---------------- SMOOTH ANIMATION ---------------- **/
  const animate = (oldPos, newPos, duration, updateFn) => {
    const start = performance.now();
    const tick = (ts) => {
      const progress = Math.min((ts - start) / duration, 1);
      updateFn({
        latitude:
          oldPos.latitude + (newPos.latitude - oldPos.latitude) * progress,
        longitude:
          oldPos.longitude + (newPos.longitude - oldPos.longitude) * progress,
      });
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  /** ---------------- SOCKET: LOCATION UPDATES ---------------- **/
  useEffect(() => {
    const handleLocation = (data) => {
      const { lat, long } = data?.location || {};
      if (isNaN(lat) || isNaN(long)) return;

      const newPos = { latitude: lat, longitude: long };
      const driverId = data.driverId;

      setDrivers((prev) => {
        const idx = prev.findIndex((d) => d._id === driverId);

        if (idx === -1) {
          fetchSingleDriver(driverId);
          return prev;
        }

        const updated = [...prev];
        const oldPos = lastPositionsRef.current[driverId] || newPos;
        lastPositionsRef.current[driverId] = newPos;

        let driver = applyHideFilterToDriver({
          ...updated[idx],
          offline: false,
        });

        updated[idx] = driver;

        if (driver.hide) {
          if (followTargetId === driverId) setFollowTargetId(null);
          return updated;
        }

        animate(oldPos, newPos, 450, (pos) => {
          setDrivers((curr) => {
            const i = curr.findIndex((d) => d._id === driverId);
            if (i === -1) return curr;

            const list = [...curr];
            list[i] = applyHideFilterToDriver({
              ...list[i],
              location: pos,
            });
            return list;
          });
        });

        return updated;
      });
    };

    socket.on("admin:driverLocationUpdate", handleLocation);
    return () => socket.off("admin:driverLocationUpdate", handleLocation);
  }, [filter, followTargetId]);

  /** ---------------- SOCKET: ORDER UPDATE → REFRESH DRIVER ---------------- **/
  useEffect(() => {
    const handleOrderUpdate = (data) => {
      if (!data) return;
      if (data?.driverId) fetchSingleDriver(data.driverId);
    };

    socket.on("admin:ordersUpdate", handleOrderUpdate);
    return () => socket.off("admin:ordersUpdate", handleOrderUpdate);
  }, []);

  /** ---------------- OFFLINE HANDLER ---------------- **/
  useEffect(() => {
    const handleDriverOffline = ({ driverId }) => {
      setDrivers((prev) =>
        prev.map((d) =>
          d._id === driverId
            ? applyHideFilterToDriver({ ...d, offline: true })
            : d,
        ),
      );

      if (selectedDriver?._id === driverId) setSelectedDriver(null);
      if (followTargetId === driverId) setFollowTargetId(null);
    };

    socket.on("admin:driverOffline", handleDriverOffline);
    return () => socket.off("admin:driverOffline", handleDriverOffline);
  }, [selectedDriver, followTargetId]);

  /** ---------------- FILTER CHANGE ---------------- **/
  useEffect(() => {
    setDrivers((prev) => prev.map(applyHideFilterToDriver));
  }, [filter]);

  /** ---------------- POPUP SYNC ---------------- **/
  useEffect(() => {
    if (!selectedDriver) return;
    const updated = drivers.find((d) => d._id === selectedDriver._id);
    if (updated) setSelectedDriver(updated);
  }, [drivers]);

  /** ---------------- INITIAL LOAD ---------------- **/
  useEffect(() => {
    fetchDrivers();
  }, []);

  /** ---------------- COUNTS ---------------- **/
  const counts = {
    availableDriver: drivers.filter(
      (d) => !d.hide && (!d.orders || d.orders.length === 0)
    ).length,
    inDeliveryDriver: drivers.filter((d) => !d.hide && d.orders?.length > 0)
      .length,
  };

  /** ---------------- CLOSE POPUP IF DRIVER HIDDEN OR REMOVED ---------------- **/
  useEffect(() => {
    if (!selectedDriver) return;

    const match = drivers.find((d) => d._id === selectedDriver._id);

    // Driver removed (offline)
    if (!match) {
      setSelectedDriver(null);
      setFollowTargetId(null);
      return;
    }

    // Driver exists but hidden by filter
    if (match.hide) {
      setSelectedDriver(null);
      setFollowTargetId(null);
    }
  }, [drivers]);

  return { drivers, counts, updateSelectedDriver, followTargetId };
};

export default useDrivers;